import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, StateView, useAuth, useToast } from "@ozpanel/ui";

/**
 * The .tmpl files a node renders its service configuration from.
 *
 * CLAUDE.md's rule 3 says these are never compiled into the binary, and gives the reason: "they
 * live on the filesystem so a node can be fixed without a rebuild". That escape hatch had no API
 * at all — not read, not write — so the only way to use it was SSH.
 *
 * Which is what failed in the node11 drill on 2026-09-03: through the whole outage the surviving
 * node answered HTTPS in 0.3 seconds and refused every SSH connection. The fallback and the thing
 * it is a fallback for shared one point of failure, and it went unnoticed because the fallback had
 * never been reachable any other way.
 *
 * Two things this screen must keep saying, because both are ways an edit here silently does
 * nothing:
 *   - writing the file APPLIES NOTHING. The owning service renders it at its next reconcile.
 *   - a deploy REPLACES it. An edit not also made in the repository is lost at the next release.
 */

type Tpl = {
  name: string;
  bytes: number;
  modified?: string;
  sha256?: string;
  raw?: string;
};

export function RenderTemplates() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState<{ name: string; raw: string; sha?: string } | null>(null);

  const q = useQuery({
    queryKey: ["render-templates"],
    queryFn: () =>
      client.apiFetch<{ directory: string; items: Tpl[] | null; total: number; note?: string }>(
        "/ozpanel/render-templates",
      ),
  });

  const open = async (name: string) => {
    try {
      const d = await client.apiFetch<Tpl>(`/ozpanel/render-templates/${name}`);
      // Refuse to open on an empty read rather than offering a blank box. An empty read and an
      // empty file are indistinguishable from here, and only one of them is safe to save over the
      // configuration a service starts from — the same rule the tinc.conf editor learned.
      if (!d?.raw?.trim()) {
        notify({
          kind: "error",
          title: `${name} came back empty`,
          body: "Refusing to open an editor whose Save would write a blank template over a live service's configuration source.",
        });
        return;
      }
      setEditing({ name, raw: d.raw, sha: d.sha256 });
    } catch (e) {
      notify({ kind: "error", title: `Could not read ${name}`, body: e instanceof Error ? e.message : String(e) });
    }
  };

  const cols: DataListColumn<Tpl>[] = [
    { key: "name", label: "Template", sortable: true, render: (t) => <span className="font-mono text-[12px]">{t.name}</span> },
    { key: "bytes", label: "Size", sortable: true, value: (t) => t.bytes, render: (t) => `${t.bytes} B` },
    {
      key: "modified",
      label: "Modified",
      sortable: true,
      render: (t) => <span className="text-[11.5px] text-muted">{t.modified?.replace("T", " ").replace("Z", "") ?? "—"}</span>,
    },
    { key: "sha256", label: "Checksum", render: (t) => <span className="font-mono text-[11px] text-muted">{t.sha256}</span> },
  ];

  return (
    <>
      <Section
        title="Service configuration templates"
        hint={q.data ? `${q.data.total} file(s)` : undefined}
        bodyClassName="p-0"
      >
        <div className="p-4">
          <StateView loading={q.isLoading} error={q.error} errorContext="the render templates">
            <p className="mb-3 max-w-3xl text-[12.5px] leading-relaxed text-muted">
              These are what this node turns into Caddy, Postfix, Dovecot and PHP-FPM configuration.
              They are deliberately <strong>not</strong> compiled into the binary, so a node can be
              repaired without a rebuild — this screen is that repair path, and until now it existed
              only over SSH.
              {q.data?.directory && (
                <>
                  {" "}
                  On disk at <span className="font-mono">{q.data.directory}</span>.
                </>
              )}
            </p>
            {/* Not the same thing as /templates, and the word has now collided twice. */}
            <p className="mb-3 max-w-3xl text-[12px] leading-relaxed text-muted">
              Not to be confused with <em>provisioning templates</em>, which decide what a new tenant
              receives. These render service config; those describe a customer.
            </p>
            <DataList
              items={q.data?.items ?? []}
              columns={cols}
              getRowKey={(t) => t.name}
              searchable={(q.data?.items?.length ?? 0) > 8}
              emptyMessage="No templates found on this node."
              emptyHint="They are deployed alongside the binary — an empty list means the deploy did not carry templates/, which is the same fault that leaves migrations missing."
              rowActions={(t) => (
                <Button size="sm" variant="ghost" onClick={() => void open(t.name)}>
                  Edit
                </Button>
              )}
            />
          </StateView>
        </div>
      </Section>

      <ConfirmDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing?.name ?? ""}
        confirmLabel="Save the template"
        destructive
        body={
          <div className="space-y-2">
            <textarea
              value={editing?.raw ?? ""}
              onChange={(e) => setEditing(editing ? { ...editing, raw: e.target.value } : null)}
              spellCheck={false}
              className="h-80 w-full rounded border border-line bg-bg p-2 font-mono text-[11.5px]"
            />
            <p className="rounded border border-warn/40 bg-warn/10 px-2 py-1.5 text-[12px] leading-relaxed">
              <strong>Saving applies nothing.</strong> The owning service reads this file at its next
              reconcile or reconfigure — until then the running configuration is unchanged, and a
              template that is correct here can still be absent from what the daemon is using.
            </p>
            <p className="text-[12px] leading-relaxed text-muted">
              A deploy <strong>replaces</strong> this file. An edit made only here is lost at the next
              release, so make the same change in the repository — otherwise the node is repaired and
              the next upgrade undoes it silently.
            </p>
          </div>
        }
        onConfirm={() => {
          const t = editing;
          setEditing(null);
          if (!t?.raw.trim()) return;
          void client
            .apiFetch<{ note?: string }>(`/ozpanel/render-templates/${t.name}`, {
              method: "PUT",
              body: JSON.stringify({ raw: t.raw }),
            })
            .then((r) => {
              notify({ kind: "success", title: `${t.name} written`, body: r?.note });
              void qc.invalidateQueries({ queryKey: ["render-templates"] });
            })
            .catch((e: unknown) =>
              notify({
                kind: "error",
                title: `Could not write ${t.name}`,
                body: e instanceof Error ? e.message : String(e),
              }),
            );
        }}
      />
    </>
  );
}
